import type { ConnectedAccount } from '@/services/firebase/firestore';
import { getConnectedAccount } from '@/services/firebase/firestore';
import { useAuthStore } from '@/store';
import { useEffect, useState } from 'react';

export interface UseConnectedAccountResult {
  connectedAccount: ConnectedAccount | null;
  loading: boolean;
  error: string | null;
  onboardUid: string | null;
}

export const useConnectedAccount = (): UseConnectedAccountResult => {
  const { user } = useAuthStore();
  const [state, setState] = useState<{
    connectedAccount: ConnectedAccount | null;
    loading: boolean;
    error: string | null;
  }>({
    connectedAccount: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    getConnectedAccount(user.uid)
      .then((account) => {
        if (!cancelled) {
          setState({ connectedAccount: account, loading: false, error: null });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Connected account fetch error:', err);
          setState({
            connectedAccount: null,
            loading: false,
            error: err.message || 'Failed to load connected account',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Derive: when there's no user, return reset values without a synchronous setState in effect
  const effectiveState = user ? state : { connectedAccount: null, loading: false, error: null };

  return {
    connectedAccount: effectiveState.connectedAccount,
    loading: effectiveState.loading,
    error: effectiveState.error,
    onboardUid: effectiveState.connectedAccount?.onboard_uid || null,
  };
};

export default useConnectedAccount;
